import React, { useEffect, useRef } from 'react';
import { useKeyboardNavigation } from '../../contexts/KeyboardNavigationContext';
import TabBar from './TabBar';

/**
 * MasterDetailLayout Component
 *
 * Provides a consistent layout for master-detail pages with tabs and toolbars.
 * Handles tab navigation with protection against losing view state.
 *
 * @param {object} config - Configuration object containing tabs, entities, and toolbars
 * @param {string} activeTab - Currently active tab
 * @param {function} onTabChange - Callback when tab changes
 * @param {ReactNode} children - Content to render for the active tab
 */
function MasterDetailLayout({
  config,
  activeTab,
  onTabChange,
  children
}) {
  const { tabs, toolbars = {}, entities = {} } = config;
  const { registerTabs, setSelectedTabIndex } = useKeyboardNavigation();
  const tabRefs = useRef([]);

  // Register tabs for keyboard navigation
  useEffect(() => {
    if (tabRefs.current.length > 0) {
      registerTabs(tabRefs.current);
    }
  }, [registerTabs, tabs]);

  // Update selected tab index when activeTab changes
  useEffect(() => {
    const index = tabs.findIndex(tab => tab.id === activeTab);
    if (index >= 0) {
      setSelectedTabIndex(index);
    }
  }, [activeTab, tabs, setSelectedTabIndex]);

  // Handle tab changes with same-tab click protection
  const handleTabChange = (tabId) => {
    // If clicking the same tab, check if any entity is in view mode
    if (tabId === activeTab) {
      // Check all entities to see if any are in view mode
      const entityStates = Object.values(entities);
      const inViewMode = entityStates.some(entity =>
        entity && (entity.viewMode === 'view' || entity.isViewing || entity.isEditing || entity.isCreating)
      );

      // Don't reset if we're viewing/editing something
      if (inViewMode) {
        return;
      }
    }

    // Reset all entities when switching tabs
    if (tabId !== activeTab) {
      Object.values(entities).forEach(entity => {
        if (entity && entity.reset) {
          entity.reset();
        }
      });
    }

    onTabChange(tabId);
  };

  // Get current toolbar based on active tab
  const currentToolbar = toolbars[activeTab] || null;

  return (
    <>
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        toolbar={currentToolbar}
        tabRefs={tabRefs}
      />
      {children}
    </>
  );
}

export default MasterDetailLayout;